// whipEncodingSettings.ts — Réglages d'encodage du flux programme (régie → MediaMTX en WHIP).
//
// Mémorisés par chaîne et par poste (localStorage), comme les réglages du réalisateur
// auto. Le module garde en plus les réglages de la chaîne affichée : api_whip les lit
// au démarrage et s'y abonne pour appliquer à chaud ce qui peut l'être.
//
// À chaud (setParameters, sans renégociation) : débit, cadence, réduction de résolution,
// préférence de dégradation, priorité réseau, indice de contenu, débit Opus.
// Au prochain « On air » seulement (renégociation SDP) : profil H264, stéréo, FEC, DTX.

import { DEFAULT_CANVAS_CONFIG } from './canvasPipeline/types';

export type WhipVideoProfile = 'auto' | 'baseline' | 'main' | 'high';

export interface WhipEncodingSettings {
  /** Plafond vidéo (kb/s). */
  maxBitrateKbps         : number;
  maxFramerate           : number;
  scaleResolutionDownBy  : number;
  degradationPreference  : RTCDegradationPreference;
  contentHint            : '' | 'motion' | 'detail' | 'text';
  networkPriority        : RTCPriorityType;
  /** 'auto' = High d'abord, barème de h264ProfilePriority à défaut (cf. whipProfilePrefix). */
  videoProfile           : WhipVideoProfile;
  /** Débit Opus visé (kb/s). */
  audioBitrateKbps       : number;
  audioStereo            : boolean;
  audioFec               : boolean;
  audioDtx               : boolean;
}

// ⚠️ Défauts changés le 2026-10-26 après le direct AKINAX (trous d'image de 1 à 2 minutes, vidéo à
// 0 i/s et son intact, MediaMTX : 2 963 erreurs RTP en une heure sur le programme).
//   - 'maintain-resolution' faisait tomber la CADENCE sous contrainte, jusqu'à 0 i/s : Chrome
//     préférait ne plus envoyer d'images plutôt que de réduire la définition. 'maintain-framerate'
//     réduit la définition et garde les 25 i/s que la chaîne aval attend.
//   - 12 Mb/s de plafond laissait l'estimateur de bande passante sonder bien au-delà de ce que le
//     lien montant d'un poste de régie supporte (≈ 2,5 Mb/s mesurés) : pertes, puis effondrement.
//     5 Mb/s suffisent largement à du 1080p25 H.264.
// Ne s'applique qu'aux postes qui n'ont jamais enregistré leurs propres réglages (engrenage ▸
// Encodage de la diffusion) : un réglage enregistré l'emporte.
export const DEFAULT_WHIP_ENCODING: WhipEncodingSettings = {
  maxBitrateKbps        : 5_000,
  maxFramerate          : DEFAULT_CANVAS_CONFIG.targetFps,
  scaleResolutionDownBy : 1,
  degradationPreference : 'maintain-framerate',
  contentHint           : 'motion',
  networkPriority       : 'high',
  videoProfile          : 'auto',
  audioBitrateKbps      : 192,
  audioStereo           : true,
  audioFec              : false,
  audioDtx              : false,
};

export const WHIP_ENCODING_LIMITS = {
  maxBitrateKbps        : { min: 1_000, max: 30_000, step: 500 },
  maxFramerate          : { min: 10,    max: 60,     step: 1 },
  scaleResolutionDownBy : { min: 1,     max: 4,      step: 0.25 },
  audioBitrateKbps      : { min: 32,    max: 510,    step: 16 },
};

/**
 * Préfixe de profile-level-id visé par chaque profil.
 *
 * 'auto' vise High : Chrome n'encode le Constrained Baseline qu'avec OpenH264 (logiciel),
 * même sur un Mac doté de VideoToolbox. Mesuré le 21/10/2026 sur un même poste : Baseline
 * → OpenH264 à 1-12 i/s, High → VideoToolbox à 22-25 i/s. Un navigateur sans High (Chrome
 * Windows sans encodeur matériel) retombe sur le barème de h264ProfilePriority.
 */
export const whipProfilePrefix = (p: WhipVideoProfile): string =>
  p === 'baseline' ? '42e0' : p === 'main' ? '4d00' : '640';

const clamp = (v: unknown, { min, max }: { min: number; max: number }, dflt: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : dflt;
const oneOf = <T extends string>(v: unknown, allowed: readonly T[], dflt: T): T =>
  allowed.includes(v as T) ? (v as T) : dflt;

export function sanitizeWhipEncoding(raw: unknown): WhipEncodingSettings {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<WhipEncodingSettings>;
  const D = DEFAULT_WHIP_ENCODING, L = WHIP_ENCODING_LIMITS;
  return {
    maxBitrateKbps        : clamp(r.maxBitrateKbps, L.maxBitrateKbps, D.maxBitrateKbps),
    maxFramerate          : clamp(r.maxFramerate, L.maxFramerate, D.maxFramerate),
    scaleResolutionDownBy : clamp(r.scaleResolutionDownBy, L.scaleResolutionDownBy, D.scaleResolutionDownBy),
    degradationPreference : oneOf(r.degradationPreference, ['maintain-resolution', 'maintain-framerate', 'balanced'] as const, D.degradationPreference),
    contentHint           : oneOf(r.contentHint, ['', 'motion', 'detail', 'text'] as const, D.contentHint),
    networkPriority       : oneOf(r.networkPriority, ['very-low', 'low', 'medium', 'high'] as const, D.networkPriority),
    videoProfile          : oneOf(r.videoProfile, ['auto', 'baseline', 'main', 'high'] as const, D.videoProfile),
    audioBitrateKbps      : clamp(r.audioBitrateKbps, L.audioBitrateKbps, D.audioBitrateKbps),
    audioStereo           : typeof r.audioStereo === 'boolean' ? r.audioStereo : D.audioStereo,
    audioFec              : typeof r.audioFec    === 'boolean' ? r.audioFec    : D.audioFec,
    audioDtx              : typeof r.audioDtx    === 'boolean' ? r.audioDtx    : D.audioDtx,
  };
}

/** Émis quand un chargement de deck remplace les réglages mémorisés : le hook les relit. */
export const WHIP_ENCODING_RESTORED_EVENT = 'regie-whip-encoding-restored';

export const whipEncodingStorageKey = (channelId: number | null) =>
  channelId && channelId > 0 ? `mediartc_regie_whip_encoding:${channelId}` : 'mediartc_regie_whip_encoding';

export function loadWhipEncoding(key: string): WhipEncodingSettings {
  try {
    const raw = localStorage.getItem(key);
    return raw ? sanitizeWhipEncoding(JSON.parse(raw)) : DEFAULT_WHIP_ENCODING;
  } catch { return DEFAULT_WHIP_ENCODING; }
}

export function saveWhipEncoding(key: string, s: WhipEncodingSettings): void {
  try { localStorage.setItem(key, JSON.stringify(s)); } catch { /* stockage indisponible */ }
}

// ── Réglages en vigueur pour la chaîne affichée ─────────────────────────────

let active: WhipEncodingSettings = DEFAULT_WHIP_ENCODING;
const listeners = new Set<(s: WhipEncodingSettings) => void>();

export const getActiveWhipEncoding = (): WhipEncodingSettings => active;

export function setActiveWhipEncoding(s: WhipEncodingSettings): void {
  active = s;
  listeners.forEach(l => { try { l(s); } catch { /* un abonné ne bloque pas les autres */ } });
}

export function subscribeWhipEncoding(l: (s: WhipEncodingSettings) => void): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}
